/**
 * Playlist Detail Page
 * Displays playlist contents with numbered list (consistent styling with CourseListPage)
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePlaylistStore } from '../stores/playlistStore';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from '../components/common/ConfirmModal';
import './PlaylistDetailPage.css';
import { BilingualTitle } from '../components/common/BilingualTitle';

const formatDuration = (seconds?: number): string => {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

const getCefrBadgeClass = (cefrLevel?: string): string => {
  if (!cefrLevel) return 'unknown';
  return `cefr-${cefrLevel.toLowerCase()}`;
};

const PlaylistDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['playlist', 'common']);
  const { user } = useAuth();
  const {
    selectedPlaylist: currentPlaylist,
    loading,
    error,
    fetchPlaylistById: fetchPlaylist,
    updatePlaylist,
    removeCourseFromPlaylist,
  } = usePlaylistStore();

  // Check if current user owns this playlist
  const isOwner = currentPlaylist?.userId === user?.id;

  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsPublic, setEditIsPublic] = useState(false);

  // Remove course confirmation modal state
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [courseToRemove, setCourseToRemove] = useState<{ id: string; title: string } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchPlaylist(id);
    }
  }, [id, fetchPlaylist]);

  useEffect(() => {
    if (currentPlaylist) {
      setEditTitle(currentPlaylist.title);
      setEditDescription(currentPlaylist.description || '');
      setEditIsPublic(currentPlaylist.isPublic);
    }
  }, [currentPlaylist]);

  const handleSaveEdit = async () => {
    if (!id || !editTitle.trim()) return;

    try {
      await updatePlaylist(id, {
        title: editTitle.trim(),
        description: editDescription.trim() || undefined,
        isPublic: editIsPublic,
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update playlist:', err);
    }
  };

  const handleRemoveClick = (courseId: string, courseTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCourseToRemove({ id: courseId, title: courseTitle });
    setRemoveModalOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!id || !courseToRemove) return;

    setIsRemoving(true);
    try {
      await removeCourseFromPlaylist(id, courseToRemove.id);
      setRemoveModalOpen(false);
      setCourseToRemove(null);
    } catch (err) {
      console.error('Failed to remove course:', err);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCancelRemove = () => {
    setRemoveModalOpen(false);
    setCourseToRemove(null);
  };

  if (loading && !currentPlaylist) {
    return (
      <div className="playlist-detail-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('playlist:detail.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="playlist-detail-page">
        <div className="error-container">
          <h2>{t('playlist:detail.error')}</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/playlists')}>
            {t('playlist:detail.backToPlaylists')}
          </button>
        </div>
      </div>
    );
  }

  if (!currentPlaylist) {
    return (
      <div className="playlist-detail-page">
        <div className="error-container">
          <h2>{t('playlist:detail.notFound')}</h2>
          <button onClick={() => navigate('/playlists')}>
            {t('playlist:detail.backToPlaylists')}
          </button>
        </div>
      </div>
    );
  }

  const courses = currentPlaylist.playlistCourses
    ?.map((pc) => pc.course)
    .filter((course): course is NonNullable<typeof course> => course != null) || [];

  return (
    <div className="playlist-detail-page">
      <div className="playlist-detail-header">
        <button className="btn-back" onClick={() => navigate('/playlists')}>
          {t('playlist:detail.backToPlaylists')}
        </button>

        {isEditing && isOwner ? (
          <div className="edit-form">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder={t('playlist:detail.titlePlaceholder')}
              className="edit-title-input"
              maxLength={100}
            />
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder={t('playlist:detail.descriptionPlaceholder')}
              className="edit-description-input"
              rows={3}
              maxLength={500}
            />
            <label className="visibility-toggle">
              <input
                type="checkbox"
                checked={editIsPublic}
                onChange={(e) => setEditIsPublic(e.target.checked)}
              />
              <span>{t('playlist:detail.publicPlaylist')}</span>
            </label>
            <div className="edit-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setIsEditing(false);
                  setEditTitle(currentPlaylist.title);
                  setEditDescription(currentPlaylist.description || '');
                  setEditIsPublic(currentPlaylist.isPublic);
                }}
              >
                {t('common:cancel')}
              </button>
              <button
                className="btn-save"
                onClick={handleSaveEdit}
                disabled={!editTitle.trim()}
              >
                {t('common:save')}
              </button>
            </div>
          </div>
        ) : (
          <div className="header-content">
            <div className="header-info">
              <h1>{currentPlaylist.title}</h1>
              {currentPlaylist.description && (
                <p className="playlist-description">
                  {currentPlaylist.description}
                </p>
              )}
              <div className="playlist-meta">
                <span className="course-count">
                  {courses.length === 1
                    ? t('playlist:courseCountSingular', { count: courses.length })
                    : t('playlist:courseCount', { count: courses.length })}
                </span>
                <span
                  className={`visibility-badge ${currentPlaylist.isPublic ? 'public' : 'private'}`}
                >
                  {currentPlaylist.isPublic ? t('playlist:public') : t('playlist:private')}
                </span>
              </div>
            </div>
            {isOwner && (
              <button className="btn-edit" onClick={() => setIsEditing(true)}>
                {t('playlist:detail.editPlaylist')}
              </button>
            )}
          </div>
        )}
      </div>

      {courses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📚</div>
          <h3>{t('playlist:detail.emptyCourses')}</h3>
          <p>{t('playlist:detail.emptyCoursesDescription')}</p>
          <Link to="/courses" className="btn-primary">
            {t('playlist:detail.browseCourses')}
          </Link>
        </div>
      ) : (
        <div className="courses-list">
          {courses.map((course, index) => (
            <div
              key={course.id}
              className="course-item"
              onClick={() => navigate(`/courses/${course.id}`)}
            >
              {/* Order number */}
              <div className="course-order">{index + 1}</div>

              {/* Thumbnail */}
              <div className="course-thumbnail">
                {(course.coverImageUrl || course.thumbnailUrl) ? (
                  <img src={course.coverImageUrl || course.thumbnailUrl} alt="" />
                ) : (
                  <div className="thumbnail-placeholder">
                    <span>🎓</span>
                  </div>
                )}
              </div>

              {/* Course info */}
              <div className="course-info">
                <BilingualTitle
                  title={course.title}
                  titles={course.titles}
                  fallback={t('playlist:detail.untitledCourse')}
                  variant="card"
                  as="h3"
                  className="course-title"
                />
                <p className="course-description">
                  {course.description || t('playlist:detail.noDescription')}
                </p>
                <div className="course-meta">
                  {course.cefrLevel && (
                    <span className={`badge difficulty ${getCefrBadgeClass(course.cefrLevel)}`}>
                      {course.cefrLevel}
                    </span>
                  )}
                  {course.durationSeconds != null && course.durationSeconds >= 30 && (
                    <span className="badge duration">
                      {formatDuration(course.durationSeconds)}
                    </span>
                  )}
                  {(course.wordCount !== undefined && course.wordCount > 0) && (
                    <span className="badge words">{t('playlist:detail.words', { count: course.wordCount })}</span>
                  )}
                </div>
                <div className="course-stats">
                  <span className="stat">
                    <span className="stat-icon">👥</span>
                    {t('playlist:detail.enrolled', { count: course.enrollmentCount || 0 })}
                  </span>
                  {course.ratingAverage != null && course.ratingAverage > 0 && course.ratingCount != null && course.ratingCount >= 3 && (
                    <span className="stat rating-stat">
                      <span className="stat-icon">⭐</span>
                      {Number(course.ratingAverage).toFixed(1)}
                      <span className="rating-count">({course.ratingCount})</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Remove button */}
              {isOwner && (
                <button
                  className="btn-remove"
                  onClick={(e) => handleRemoveClick(course.id, course.title || t('playlist:detail.untitledCourse'), e)}
                  title={t('playlist:detail.removeFromPlaylist')}
                  aria-label={t('playlist:detail.removeFromPlaylist')}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Remove Course Confirmation Modal */}
      <ConfirmModal
        isOpen={removeModalOpen}
        title={t('playlist:detail.removeTitle')}
        message={t('playlist:detail.removeMessage', { title: courseToRemove?.title })}
        confirmText={t('playlist:detail.remove')}
        cancelText={t('common:cancel')}
        confirmVariant="danger"
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
        loading={isRemoving}
      />
    </div>
  );
};

export default PlaylistDetailPage;
